import React, { useState, useMemo, useCallback } from 'react';
import { formatPrice } from '../utils/currency';

/**
 * ComboSelector — Componente compartilhado para seleção de combos (Sitri + Astarte)
 *
 * Props:
 *  - produto: objeto completo do combo (com combo_grupos[])
 *  - onComplete(result): chamado com { valor, grupos: [{id_grupo, selecoes: [{id_item, quantidade, adicionais}]}] }
 *  - onClose: chamado quando o cliente fecha
 */
function ComboSelector({ produto, onComplete, onClose }) {
  // Estado de seleções por grupo: { [id_grupo]: { [id_item]: { quantidade, adicionais: [] } } }
  const [selecoesPorGrupo, setSelecoesPorGrupo] = useState({});
  const [personalizandoItem, setPersonalizandoItem] = useState(null); // { grupoId, item }

  const grupos = produto?.combo_grupos || [];

  // Calcula o total de seleções para um grupo
  const getGrupoTotal = useCallback((grupoId) => {
    const selecoes = selecoesPorGrupo[grupoId] || {};
    return Object.values(selecoes).reduce((sum, sel) => sum + (sel.quantidade || 0), 0);
  }, [selecoesPorGrupo]);

  // Calcula o valor adicional total
  const valorAdicional = useMemo(() => {
    let total = 0;
    grupos.forEach(grupo => {
      const selecoes = selecoesPorGrupo[grupo.id] || {};
      grupo.itens?.forEach(item => {
        const sel = selecoes[item.id];
        if (sel && sel.quantidade > 0) {
          total += (parseFloat(item.valor_adicional) || 0) * sel.quantidade;
          // Somar adicionais do item
          if (sel.adicionais && sel.adicionais.length > 0) {
            sel.adicionais.forEach(ad => {
              total += (parseFloat(ad.valor) || 0) * (ad.quantidade || 1);
            });
          }
        }
      });
    });
    return total;
  }, [selecoesPorGrupo, grupos]);

  // Preço total do combo
  const valorTotal = useMemo(() => {
    return (parseFloat(produto?.valor) || 0) + valorAdicional;
  }, [produto, valorAdicional]);

  // Verifica se todos os grupos atendem o mínimo de seleções
  const todosGruposValidos = useMemo(() => {
    return grupos.every(grupo => {
      const minSelecoes = grupo.min_selecoes || 1;
      return getGrupoTotal(grupo.id) >= minSelecoes;
    });
  }, [grupos, getGrupoTotal]);

  // Selecionar/deselecionar item de um grupo
  const toggleItem = useCallback((grupo, item) => {
    setSelecoesPorGrupo(prev => {
      const selecoes = { ...(prev[grupo.id] || {}) };
      const maxSelecoes = grupo.max_selecoes || 1;

      if (selecoes[item.id] && selecoes[item.id].quantidade > 0) {
        // Deselecionar
        const { [item.id]: _, ...resto } = selecoes;
        return { ...prev, [grupo.id]: resto };
      }

      // Se max = 1, substitui a seleção
      if (maxSelecoes === 1) {
        return {
          ...prev,
          [grupo.id]: { [item.id]: { quantidade: 1, adicionais: [] } }
        };
      }

      // Se já atingiu o máximo, não adicionar
      const totalAtual = Object.values(selecoes).reduce((sum, s) => sum + (s.quantidade || 0), 0);
      if (totalAtual >= maxSelecoes) {
        return prev;
      }

      // Adicionar
      return {
        ...prev,
        [grupo.id]: {
          ...selecoes,
          [item.id]: { quantidade: 1, adicionais: [] }
        }
      };
    });
  }, []);

  // Incrementar quantidade de um item
  const incrementarItem = useCallback((grupo, item) => {
    setSelecoesPorGrupo(prev => {
      const selecoes = { ...(prev[grupo.id] || {}) };
      const maxSelecoes = grupo.max_selecoes || 1;
      const totalAtual = Object.values(selecoes).reduce((sum, s) => sum + (s.quantidade || 0), 0);

      if (totalAtual >= maxSelecoes) return prev;

      const atual = selecoes[item.id] || { quantidade: 0, adicionais: [] };
      return {
        ...prev,
        [grupo.id]: {
          ...selecoes,
          [item.id]: { ...atual, quantidade: atual.quantidade + 1 }
        }
      };
    });
  }, []);

  // Decrementar quantidade de um item
  const decrementarItem = useCallback((grupo, item) => {
    setSelecoesPorGrupo(prev => {
      const selecoes = { ...(prev[grupo.id] || {}) };
      const atual = selecoes[item.id];
      if (!atual || atual.quantidade <= 0) return prev;

      if (atual.quantidade === 1) {
        const { [item.id]: _, ...resto } = selecoes;
        return { ...prev, [grupo.id]: resto };
      }

      return {
        ...prev,
        [grupo.id]: {
          ...selecoes,
          [item.id]: { ...atual, quantidade: atual.quantidade - 1 }
        }
      };
    });
  }, []);

  // Salvar adicionais de personalização
  const salvarAdicionais = useCallback((grupoId, itemId, adicionais) => {
    setSelecoesPorGrupo(prev => {
      const selecoes = { ...(prev[grupoId] || {}) };
      const atual = selecoes[itemId] || { quantidade: 1, adicionais: [] };
      return {
        ...prev,
        [grupoId]: {
          ...selecoes,
          [itemId]: { ...atual, adicionais }
        }
      };
    });
    setPersonalizandoItem(null);
  }, []);

  // Handler de submit
  const handleAdicionar = useCallback(() => {
    if (!todosGruposValidos) return;

    const gruposResult = grupos.map(grupo => {
      const selecoes = selecoesPorGrupo[grupo.id] || {};
      return {
        id_grupo: grupo.id,
        selecoes: Object.entries(selecoes)
          .filter(([_, sel]) => sel.quantidade > 0)
          .map(([id_item, sel]) => ({
            id_item: parseInt(id_item),
            quantidade: sel.quantidade,
            adicionais: sel.adicionais || []
          }))
      };
    });

    onComplete({
      valor: valorTotal,
      grupos: gruposResult
    });
  }, [todosGruposValidos, grupos, selecoesPorGrupo, valorTotal, onComplete]);

  // ---- RENDER ----

  if (!produto || !grupos.length) return null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onClose} style={styles.closeBtn} aria-label="Fechar">
          ×
        </button>
        <h2 style={styles.title}>{produto.nome}</h2>
        {produto.descricao && (
          <p style={styles.description}>{produto.descricao}</p>
        )}
        <div style={styles.priceTag}>
          {formatPrice(produto.valor)}
        </div>
      </div>

      {/* Grupos de seleção */}
      <div style={styles.groupsContainer}>
        {grupos.map(grupo => {
          const total = getGrupoTotal(grupo.id);
          const minSelecoes = grupo.min_selecoes || 1;
          const maxSelecoes = grupo.max_selecoes || 1;
          const grupoValido = total >= minSelecoes;

          return (
            <div key={grupo.id} style={styles.group}>
              {/* Header do grupo */}
              <div style={styles.groupHeader}>
                <div style={styles.groupTitleRow}>
                  <h3 style={styles.groupTitle}>{grupo.nome}</h3>
                  {minSelecoes > 0 && (
                    <span style={styles.obrigatorioTag}>OBRIGATÓRIO</span>
                  )}
                </div>
                <div style={styles.groupMeta}>
                  <span style={styles.groupCount}>
                    {total}/{maxSelecoes}
                  </span>
                  {grupoValido && (
                    <span style={styles.checkBadge}>✓</span>
                  )}
                </div>
              </div>

              {/* Itens do grupo */}
              <div style={styles.itemsGrid}>
                {(grupo.itens || []).filter(item => item.b_ativo !== 0 && item.b_ativo !== '0').map(item => {
                  const selecao = (selecoesPorGrupo[grupo.id] || {})[item.id];
                  const isSelected = selecao && selecao.quantidade > 0;
                  const temAdicionais = item.produto?.gruposAdicionais && item.produto.gruposAdicionais.length > 0;

                  return (
                    <div
                      key={item.id}
                      style={{
                        ...styles.itemCard,
                        ...(isSelected ? styles.itemCardSelected : {})
                      }}
                      onClick={() => {
                        if (maxSelecoes === 1) {
                          toggleItem(grupo, item);
                        } else if (!isSelected) {
                          incrementarItem(grupo, item);
                        }
                      }}
                    >
                      {/* Imagem do produto */}
                      {item.produto?.imagem && (
                        <div style={styles.itemImageContainer}>
                          <img
                            src={`https://grubupload.s3.amazonaws.com/${typeof item.produto.imagem === 'string' ? item.produto.imagem : item.produto.imagem.imagem}`}
                            alt={item.produto?.nome || item.nome}
                            style={styles.itemImage}
                            onError={(e) => {
                              e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
                            }}
                          />
                          {parseFloat(item.valor_adicional) > 0 && (
                            <span style={styles.adicionalBadge}>
                              +{formatPrice(item.valor_adicional)}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Info do item */}
                      <div style={styles.itemInfo}>
                        <span style={styles.itemName}>
                          {item.produto?.nome || item.nome}
                        </span>
                        {!item.produto?.imagem && parseFloat(item.valor_adicional) > 0 && (
                          <span style={styles.adicionalInline}>
                            +{formatPrice(item.valor_adicional)}
                          </span>
                        )}
                      </div>

                      {/* Controle de quantidade (quando max > 1) */}
                      {maxSelecoes > 1 && isSelected && (
                        <div style={styles.qtyControls} onClick={e => e.stopPropagation()}>
                          <button
                            style={styles.qtyBtn}
                            onClick={() => decrementarItem(grupo, item)}
                            aria-label="Remover"
                          >
                            −
                          </button>
                          <span style={styles.qtyValue}>{selecao.quantidade}</span>
                          <button
                            style={{
                              ...styles.qtyBtn,
                              ...(total >= maxSelecoes ? { opacity: 0.4, cursor: 'not-allowed' } : {})
                            }}
                            onClick={() => incrementarItem(grupo, item)}
                            disabled={total >= maxSelecoes}
                            aria-label="Adicionar"
                          >
                            +
                          </button>
                        </div>
                      )}

                      {/* Indicador de seleção (quando max = 1) */}
                      {maxSelecoes === 1 && (
                        <div style={{
                          ...styles.radioIndicator,
                          ...(isSelected ? styles.radioSelected : {})
                        }}>
                          {isSelected && <span style={styles.radioCheck}>✓</span>}
                        </div>
                      )}

                      {/* Botão personalizar */}
                      {isSelected && temAdicionais && (
                        <button
                          style={styles.personalizarBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPersonalizandoItem({ grupoId: grupo.id, item });
                          }}
                        >
                          Personalizar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer fixo com preço e botão */}
      <div style={styles.footer}>
        <div style={styles.footerPrice}>
          <span style={styles.footerLabel}>Total</span>
          <span style={styles.footerValue}>{formatPrice(valorTotal)}</span>
          {valorAdicional > 0 && (
            <span style={styles.footerAdicional}>
              (combo {formatPrice(produto.valor)} + adicionais {formatPrice(valorAdicional)})
            </span>
          )}
        </div>
        <button
          style={{
            ...styles.addBtn,
            ...(todosGruposValidos ? {} : styles.addBtnDisabled)
          }}
          disabled={!todosGruposValidos}
          onClick={handleAdicionar}
        >
          Adicionar ao carrinho
        </button>
      </div>

      {/* Modal de personalização de adicionais */}
      {personalizandoItem && (
        <PersonalizarOverlay
          item={personalizandoItem.item}
          grupoId={personalizandoItem.grupoId}
          adicionaisAtuais={(selecoesPorGrupo[personalizandoItem.grupoId] || {})[personalizandoItem.item.id]?.adicionais || []}
          onSave={salvarAdicionais}
          onClose={() => setPersonalizandoItem(null)}
        />
      )}
    </div>
  );
}

/**
 * Overlay para personalização de adicionais de um item dentro do combo
 */
function PersonalizarOverlay({ item, grupoId, adicionaisAtuais, onSave, onClose }) {
  const [selecionados, setSelecionados] = useState(() => {
    // Inicializar com adicionais já selecionados
    const mapa = {};
    adicionaisAtuais.forEach(ad => {
      mapa[ad.id] = ad.quantidade || 1;
    });
    return mapa;
  });

  const gruposAdicionais = item.produto?.gruposAdicionais || [];

  const toggleAdicional = (grupo, opcional) => {
    setSelecionados(prev => {
      if (grupo.max === 1) {
        // Radio: limpa seleções do grupo e seleciona este
        const novoMapa = { ...prev };
        grupo.grupoOpcionais?.forEach(op => {
          delete novoMapa[op.id];
        });
        if (!prev[opcional.id]) {
          novoMapa[opcional.id] = 1;
        }
        return novoMapa;
      }

      // Toggle checkbox
      if (prev[opcional.id]) {
        const { [opcional.id]: _, ...rest } = prev;
        return rest;
      }

      // Verifica max do grupo
      const totalGrupo = (grupo.grupoOpcionais || []).reduce(
        (sum, op) => sum + (prev[op.id] || 0), 0
      );
      if (grupo.max && totalGrupo >= grupo.max) return prev;

      return { ...prev, [opcional.id]: 1 };
    });
  };

  const handleSalvar = () => {
    const adicionais = [];
    gruposAdicionais.forEach(grupo => {
      (grupo.grupoOpcionais || []).forEach(opcional => {
        const qtd = selecionados[opcional.id];
        if (qtd && qtd > 0) {
          adicionais.push({
            id: opcional.id,
            nome: opcional.nome,
            valor: opcional.valor,
            quantidade: qtd
          });
        }
      });
    });
    onSave(grupoId, item.id, adicionais);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.overlayContent}>
        <div style={styles.overlayHeader}>
          <h3 style={styles.overlayTitle}>
            Personalizar: {item.produto?.nome || item.nome}
          </h3>
          <button onClick={onClose} style={styles.overlayClose} aria-label="Fechar personalização">×</button>
        </div>

        <div style={styles.overlayBody}>
          {gruposAdicionais.map(grupo => (
            <div key={grupo.id} style={styles.adGrupo}>
              <div style={styles.adGrupoHeader}>
                <span style={styles.adGrupoNome}>{grupo.nome}</span>
                {grupo.max && (
                  <span style={styles.adGrupoMeta}>Máx: {grupo.max}</span>
                )}
              </div>
              {(grupo.grupoOpcionais || []).filter(op => op.b_ativo !== 0 && op.b_ativo !== '0').map(opcional => {
                const isSel = !!selecionados[opcional.id];
                return (
                  <div
                    key={opcional.id}
                    style={{
                      ...styles.adItem,
                      ...(isSel ? styles.adItemSelected : {})
                    }}
                    onClick={() => toggleAdicional(grupo, opcional)}
                  >
                    <div style={styles.adItemCheck}>
                      {grupo.max === 1 ? (
                        <div style={{
                          ...styles.radioSmall,
                          ...(isSel ? styles.radioSmallSelected : {})
                        }}>
                          {isSel && <span style={{ color: '#fff', fontSize: '10px' }}>●</span>}
                        </div>
                      ) : (
                        <div style={{
                          ...styles.checkSmall,
                          ...(isSel ? styles.checkSmallSelected : {})
                        }}>
                          {isSel && <span style={{ color: '#fff', fontSize: '10px' }}>✓</span>}
                        </div>
                      )}
                    </div>
                    <span style={styles.adItemNome}>{opcional.nome}</span>
                    {parseFloat(opcional.valor) > 0 && (
                      <span style={styles.adItemValor}>+{formatPrice(opcional.valor)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={styles.overlayFooter}>
          <button style={styles.overlayBtnSave} onClick={handleSalvar}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- STYLES (mobile-first, inline para manter pattern do Sitri) ----
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    position: 'relative',
    padding: '20px 16px 16px',
    borderBottom: '1px solid #e5e7eb',
  },
  closeBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'rgba(53, 53, 53, 0.74)',
    border: 'none',
    color: '#e8e8e8',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    fontSize: '22px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: '0 0 6px 0',
    paddingRight: '40px',
  },
  description: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 12px 0',
  },
  priceTag: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#10b981',
  },

  // Grupos
  groupsContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 0 120px 0',
    WebkitOverflowScrolling: 'touch',
  },
  group: {
    padding: '16px',
    borderBottom: '8px solid #f5f6f8',
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px',
  },
  groupTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  groupTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0,
  },
  obrigatorioTag: {
    background: '#e3e3e3',
    color: '#a6a6a6',
    fontSize: '10px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  groupMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  groupCount: {
    fontSize: '13px',
    color: '#10b981',
    fontWeight: '600',
  },
  checkBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '#4bd183',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '700',
  },

  // Items grid
  itemsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  itemCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    border: '1.5px solid #e5e7eb',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    position: 'relative',
    flexWrap: 'wrap',
  },
  itemCardSelected: {
    borderColor: '#006aff',
    backgroundColor: '#f0f6ff',
  },
  itemImageContainer: {
    position: 'relative',
    width: '56px',
    height: '56px',
    borderRadius: '10px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  itemImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  adicionalBadge: {
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    background: '#f59e0b',
    color: '#fff',
    fontSize: '9px',
    fontWeight: '700',
    padding: '2px 4px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
  },
  itemInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  itemName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1a1a1a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  adicionalInline: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#f59e0b',
  },

  // Quantidade
  qtyControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  qtyBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#333',
  },
  qtyValue: {
    fontSize: '14px',
    fontWeight: '600',
    minWidth: '20px',
    textAlign: 'center',
  },

  // Radio
  radioIndicator: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: '2px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  radioSelected: {
    borderColor: '#006aff',
    backgroundColor: '#006aff',
  },
  radioCheck: {
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
  },

  // Personalizar
  personalizarBtn: {
    width: '100%',
    marginTop: '6px',
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #006aff',
    borderRadius: '8px',
    color: '#006aff',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Footer
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '14px 16px',
    background: '#fff',
    borderTop: '1px solid #e5e7eb',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
    zIndex: 100,
  },
  footerPrice: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    marginBottom: '10px',
    flexWrap: 'wrap',
  },
  footerLabel: {
    fontSize: '14px',
    color: '#666',
  },
  footerValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a1a1a',
  },
  footerAdicional: {
    fontSize: '11px',
    color: '#888',
  },
  addBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    background: '#006aff',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  addBtnDisabled: {
    background: '#cbd5e1',
    cursor: 'not-allowed',
  },

  // Overlay personalização
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'flex-end',
  },
  overlayContent: {
    width: '100%',
    maxHeight: '80vh',
    background: '#fff',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  overlayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 16px 12px',
    borderBottom: '1px solid #e5e7eb',
  },
  overlayTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
    color: '#1a1a1a',
  },
  overlayClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '0 4px',
  },
  overlayBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    WebkitOverflowScrolling: 'touch',
  },
  overlayFooter: {
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
  },
  overlayBtnSave: {
    width: '100%',
    padding: '13px',
    borderRadius: '12px',
    border: 'none',
    background: '#006aff',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Adicionais
  adGrupo: {
    marginBottom: '16px',
  },
  adGrupoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  adGrupoNome: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
  },
  adGrupoMeta: {
    fontSize: '12px',
    color: '#888',
  },
  adItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    marginBottom: '6px',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  adItemSelected: {
    borderColor: '#006aff',
    backgroundColor: '#f5f8ff',
  },
  adItemCheck: {
    flexShrink: 0,
  },
  radioSmall: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSmallSelected: {
    borderColor: '#006aff',
    backgroundColor: '#006aff',
  },
  checkSmall: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkSmallSelected: {
    borderColor: '#006aff',
    backgroundColor: '#006aff',
  },
  adItemNome: {
    flex: 1,
    fontSize: '13px',
    color: '#333',
  },
  adItemValor: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#f59e0b',
  },
};

export default ComboSelector;
